import { z } from 'zod'
import type { GETpropertiesByIDsResponse } from '../property/infrastructure/HTTPPropertyRepository/schemas/properties/ids'
import { Model } from './architecture'
import { Image } from './Image'
import type { POSTRelatedResponseType } from '../property/infrastructure/HTTPPropertyRepository/schemas/related'
import type { POSTBannerResponseType } from '../property/infrastructure/HTTPPropertyRepository/schemas/banner'
import type { SearchProperty } from '../property/infrastructure/HTTPPropertyRepository/schemas/v3/search/legacy'
import type { GetV3PropertyImage } from '../property/infrastructure/HTTPPropertyRepository/schemas/v3/property/:country/:city/:slug'
import type { GETAvailabilityPropertyIdResponseType } from '../booking/infrastructure/HTTPBookingRepository/schemas/v4/availability/property/:id'

const MISSING_TRANSLATION = 'MISSING TRANSLATION'

export const ListOfImageValidation = z.object({
  list: z.instanceof(Image, { message: '[ListOfImage] list is required' }).array(),
})

export class ListOfImage extends Model {
  static create({ list }: z.infer<typeof ListOfImageValidation>) {
    ListOfImageValidation.parse({ list })
    return new ListOfImage(list)
  }

  static empty() {
    return ListOfImage.create({ list: [] })
  }

  static fromAPI({ json }: { json: GETpropertiesByIDsResponse['properties'][number]['images'] }) {
    return ListOfImage.create({ list: json.map(imageJSON => Image.fromAPI({ json: imageJSON })) })
  }

  static fromJSON({ json }: { json: ReturnType<ListOfImage['toJSON']> }) {
    return ListOfImage.create({
      list: json.list.map(imageJSON => Image.fromJSON({ json: imageJSON })),
    })
  }

  static fromRelatedAPI({
    json,
  }: {
    json: POSTRelatedResponseType['properties'][number]['images']
  }) {
    return ListOfImage.create({
      list: json.map(imageJSON => Image.fromRelatedAPI({ json: imageJSON })),
    })
  }

  static fromBannerApi({ json }: { json: POSTBannerResponseType['properties'][number]['images'] }) {
    return ListOfImage.create({
      list: json.map(imageJSON => Image.fromBannerApi({ json: imageJSON })),
    })
  }

  static fromSerpApiLegacy({ json }: { json?: SearchProperty['photos'] }) {
    if (!json) return ListOfImage.empty()

    return ListOfImage.create({
      list: json.map(imageJSON => Image.fromSearchApiLegacy({ json: imageJSON })),
    })
  }

  static fromPropertyV3API({ json }: { json?: GetV3PropertyImage[] }) {
    if (!json || json.length === 0) return ListOfImage.empty()

    return ListOfImage.create({
      list: json.map(imageJSON =>
        Image.fromPrimitive({
          bucket: imageJSON.bucket,
          path: imageJSON.path,
          code: imageJSON.code,
          width: imageJSON.width,
          height: imageJSON.height,
          categoryOrder: imageJSON.categoryOrder ?? 0,
        })
      ),
    })
  }

  static fromAvailabilityApi({
    availability,
    json,
  }: {
    availability: GETAvailabilityPropertyIdResponseType[string]['availability'][number]
    json: GETAvailabilityPropertyIdResponseType
  }) {
    try {
      const roomName = availability.room_name

      const roomNameContent = json.room_content[roomName]

      const imagesFromName = roomNameContent?.images

      if (imagesFromName)
        return ListOfImage.create({
          list: imagesFromName.map(image =>
            Image.fromPrimitive({
              bucket: image.bucket,
              path: image.path,
              code: image.code,
              width: image.width,
              height: image.height,
              categoryOrder: image.order,
            })
          ),
        })

      const roomCode = availability.room_code

      const imagesFromMissingTranslation = json.room_content?.[MISSING_TRANSLATION]?.images.filter(
        image => image.room_code === roomCode
      )

      if (imagesFromMissingTranslation) {
        return ListOfImage.create({
          list: imagesFromMissingTranslation.map(image =>
            Image.fromPrimitive({
              bucket: image.bucket,
              path: image.path,
              code: image.code,
              width: image.width,
              height: image.height,
              categoryOrder: image.order,
            })
          ),
        })
      }

      return ListOfImage.empty()
    } catch {
      return ListOfImage.empty()
    }
  }
  constructor(private readonly _list: Image[]) {
    super()
  }

  get list() { return this._list } // prettier-ignore

  toJSON() {
    return { list: this._list.map(item => item.toJSON()) }
  }

  map<T>(fn: (id: Image) => T) {
    return this._list.map(fn)
  }

  at(index: number) {
    return this._list.at(index)
  }

  count() {
    return this._list.length
  }
}
